import * as T from 'three';
import { AnimatedCharacter } from '@london-life/game/characters';
import type { Character } from '@london-life/game/game';
import { createSceneKit } from '@london-life/game/scene/kit';
import { softwareRenderer } from '@london-life/game/scene/gpu';
import { RIDE_SETTING, rideProfile, seeded, type RideMode } from './path';
import { CHUNK, LANE, RAIL_TRACK, RIDE_DECK, buildChunk } from './scenery';
import {
  blackCab,
  car,
  dlrTrain,
  doubleDecker,
  mainlineTrain,
  ownCar,
  scooter,
  tubeTrain,
  van,
  walker,
} from './vehicles';

// The ride on the way somewhere, as in Lagos Life: the map gives way to the street itself. The player's cab
// or bus drives through London (the DLR on its viaduct, the Tube in its tunnel; walking along the pavement),
// past traffic, people and "Now passing" signs for the areas on the way, and stops at the place as the journey
// ends. Three ways to watch: behind (chase), from the pavement (side), or from the player's seat (inside).
// Timed by the journey the server set, so it ends when the player arrives.

export type RideView = 'chase' | 'side' | 'inside';
export type RideOptions = {
  mode: RideMode;
  // a train out of King's Cross: its line's paint
  train?: { body: string; band: string; doors: string };
  // the player's own car (mode "car"): its paint, whether it is a scooter, and whether a driver has the wheel
  car?: { paint: string; bike: boolean; driver: boolean };
  startedAt: number;
  endsAt: number;
  destination: { name: string; icon: string };
  // the areas on the way, the first where the player starts
  areas: string[];
  character: Character;
  night: boolean;
  seed: number;
  dataSaver: boolean;
  view: RideView;
};
export type Ride = { ok: boolean; setView: (view: RideView) => void; dispose: () => void };

const SKY = { day: '#bcd6ea', night: '#0f1a30', tunnel: '#0b0c0e' };
const CAR_COLOURS = ['#9aa3ad', '#2b2f36', '#f2f2f0', '#7a1f24', '#1f3f6b', '#5c6b4a', '#c8c3b5'];
const COATS = ['#2d3a4a', '#6b2b2b', '#3e5a3a', '#1d1d1d', '#a07a4a', '#4a4f7a', '#8a8f96'];
const SKINS = ['#f1c7a6', '#d9a17f', '#a8704f', '#7a4b31', '#4f2f1f'];

export function createRide(element: HTMLElement, options: RideOptions): Ride {
  let renderer: T.WebGLRenderer;
  try {
    renderer = new T.WebGLRenderer({ antialias: !options.dataSaver, powerPreference: 'low-power' });
  } catch {
    return { ok: false, setView: () => {}, dispose: () => {} };
  }
  // without a graphics chip: fewer pixels and a shorter view, so it still moves smoothly
  const slow = softwareRenderer(renderer);
  renderer.setPixelRatio(slow ? 0.6 : options.dataSaver ? 1 : Math.min(devicePixelRatio, 1.5));
  renderer.toneMapping = T.ACESFilmicToneMapping;
  const canvas = renderer.domElement;
  canvas.setAttribute('aria-hidden', 'true');
  element.appendChild(canvas);

  const setting = RIDE_SETTING[options.mode];
  const deck = RIDE_DECK[setting];
  const kit = createSceneKit();
  const scene = new T.Scene();
  const sky = setting === 'tunnel' ? SKY.tunnel : options.night ? SKY.night : SKY.day;
  scene.background = new T.Color(sky);
  scene.fog = new T.Fog(
    sky,
    setting === 'tunnel' ? 8 : 70,
    setting === 'tunnel' ? 70 : setting === 'viaduct' ? 320 : 230,
  );
  scene.add(
    options.night || setting === 'tunnel'
      ? new T.HemisphereLight('#8f9fcf', '#20242c', setting === 'tunnel' ? 1.1 : setting === 'railway' ? 1.4 : 0.9)
      : new T.HemisphereLight('#fff6e6', '#6f7f6a', 1.3),
  );
  const sun = new T.DirectionalLight(options.night ? '#aebcff' : '#ffe9c7', options.night ? 0.5 : 2.2);
  sun.position.set(-30, 60, 20);
  scene.add(sun);
  const camera = new T.PerspectiveCamera(58, 1, 0.1, 400);

  const random = seeded(options.seed);
  const span = Math.max(1, (options.endsAt - options.startedAt) / 1000);
  const profile = rideProfile(options.mode, span);
  const stop = profile.distance;
  const modern = (area: string) => /wharf|docklands|stratford|greenwich peninsula|isle of dogs|city/i.test(area);
  // the "Now passing" signs, spread along the way (the last area is the place's own, shown on its front)
  const passing = options.areas.slice(1, -1).length ? options.areas.slice(1, -1) : options.areas.slice(0, 1);
  const signs = passing.map((text, i) => ({ z: 18 + (Math.max(0, stop - 40) * (i + 0.5)) / passing.length, text }));
  const chunks = new Map<number, T.Group>();
  const plan = (index: number) => {
    const start = index * CHUNK;
    const here = signs.filter((sign) => sign.z >= start && sign.z < start + CHUNK);
    const where = [...signs].reverse().find((sign) => sign.z < start)?.text ?? options.areas[0] ?? '';
    return {
      setting,
      index,
      random: seeded(options.seed + index * 7919),
      night: options.night,
      signs: here.map((sign) => ({ at: sign.z - start, text: sign.text })),
      arrival:
        stop + 6 >= start && stop + 6 < start + CHUNK
          ? { at: stop + 6 - start, name: options.destination.name, icon: options.destination.icon }
          : null,
      modern: modern(where) || setting === 'viaduct',
    };
  };
  const keepChunks = (z: number) => {
    const first = Math.floor((z - 40) / CHUNK);
    const last = Math.floor((z + (slow ? 120 : 220)) / CHUNK);
    for (const [index, chunk] of chunks)
      if (index < first || index > last) {
        scene.remove(chunk);
        chunks.delete(index);
      }
    for (let index = Math.max(-1, first); index <= last; index += 1)
      if (!chunks.has(index)) {
        const chunk = buildChunk(kit, plan(index));
        chunks.set(index, chunk);
        scene.add(chunk);
      }
  };

  // the player's ride (or the player, walking)
  const lane = setting === 'street' ? (options.mode === 'walk' ? 5.0 : LANE) : setting === 'railway' ? RAIL_TRACK : 0;
  const ride =
    options.mode === 'cab'
      ? blackCab(kit)
      : options.mode === 'car'
        ? options.car?.bike
          ? scooter(kit, options.car.paint)
          : ownCar(kit, options.car?.paint ?? '#9aa3ad', !options.car?.driver)
        : options.mode === 'bus'
          ? doubleDecker(kit, options.destination.name)
          : options.mode === 'dlr'
            ? dlrTrain(kit)
            : options.mode === 'tube'
              ? tubeTrain(kit)
              : options.mode === 'rail'
                ? mainlineTrain(kit, options.train ?? { body: '#f2f3f5', band: '#1d3c8f', doors: '#1d3c8f' })
                : null;
  // walking: the player's own character on the pavement
  const me = options.mode === 'walk' ? new AnimatedCharacter(options.character) : null;
  if (me) {
    me.group.scale.setScalar(0.75);
    scene.add(me.group);
    void me.ready.then(() => me.setPose('walk'));
  }
  const body = ride?.group ?? me?.group ?? new T.Group();
  if (ride) scene.add(ride.group);
  body.position.set(lane, deck, 0);

  // traffic: our side going our way (ahead), the other side coming at us; people on both pavements
  type Mover = { group: T.Group; x: number; z: number; speed: number; step?: (phase: number) => void };
  const movers: Mover[] = [];
  // on the main line: now and then a train the other way on the other track
  if (setting === 'railway') {
    const other = mainlineTrain(kit, { body: '#f2f3f5', band: '#d0367f', doors: '#2b2f3a' });
    other.group.rotation.y = Math.PI;
    other.group.position.set(-RAIL_TRACK, deck, 0);
    scene.add(other.group);
    movers.push({ group: other.group, x: -RAIL_TRACK, z: 260, speed: -32 });
  }
  if (setting === 'street') {
    const level = deck;
    for (let i = 0; i < 9; i += 1) {
      const kind = random();
      const vehicle =
        kind < 0.25
          ? blackCab(kit)
          : kind < 0.4
            ? doubleDecker(kit, 'Not in service')
            : kind < 0.55
              ? van(kit)
              : car(kit, CAR_COLOURS[i % CAR_COLOURS.length]!);
      const coming = i % 3 !== 0;
      const x = coming ? -LANE : LANE;
      if (coming) vehicle.group.rotation.y = Math.PI;
      vehicle.group.position.set(x, level, 0);
      scene.add(vehicle.group);
      movers.push({
        group: vehicle.group,
        x,
        z: coming ? 40 + i * 35 : 22 + i * 24,
        speed: coming ? -(9 + random() * 5) : options.mode === 'walk' ? 6 : ourSideSpeed(options.mode, random),
      });
    }
    for (let i = 0; i < 16; i += 1) {
      const side = i % 2 ? 1 : -1;
      const person = walker(kit, COATS[i % COATS.length]!, i % 3 ? '#2a2f3a' : '#4a3b2c', SKINS[i % SKINS.length]!);
      const goingOurWay = random() < 0.5;
      if (!goingOurWay) person.group.rotation.y = Math.PI;
      const x = side * (4.5 + random() * 1.6);
      person.group.position.set(x, 0.16, 0);
      scene.add(person.group);
      movers.push({ group: person.group, x, z: -20 + i * 14, speed: goingOurWay ? 1.3 : -1.4, step: person.step });
    }
  }

  // where the camera wants to be for each view, and where it looks
  let view: RideView = options.view;
  const eye = new T.Vector3();
  const look = new T.Vector3();
  const height = setting === 'street' ? (options.mode === 'bus' ? 4.4 : options.mode === 'walk' ? 1.8 : 1.6) : 3;
  const aim = (z: number) => {
    const x = body.position.x;
    const y = body.position.y;
    if (view === 'inside') {
      const seat = ride?.eye ?? new T.Vector3(0, 1.55, 0.25);
      eye.set(x + seat.x, y + seat.y, z + seat.z);
      look.set(x + seat.x * 0.4, y + seat.y - 0.15, z + seat.z + 20);
    } else if (view === 'side' && setting === 'tunnel') {
      // no room beside a train in its tunnel: watch it come from just ahead
      const front = (ride?.length ?? 0) / 2;
      eye.set(x + 1.7, y + 2.4, z + front + 18);
      look.set(x, y + 1.2, z + front - 6);
    } else if (view === 'side' && setting === 'railway') {
      // from over the other track, the houses below: along the train's side towards its nose
      const front = (ride?.length ?? 0) / 2;
      eye.set(x - 6.2, y + 3.4, z + front - 4);
      look.set(x, y + 1.6, z - 6);
    } else if (view === 'side') {
      // far enough out to see all of it with the street behind
      const out = options.mode === 'walk' ? -3.4 : Math.max(5.5, (ride?.length ?? 4) * 0.9);
      eye.set(x + out, y + 1.7, z + 1.5);
      look.set(x, y + height * 0.45, z);
    } else if (setting === 'railway' && ride) {
      // behind and a little off to the parapet's side: the train's side and its lit windows, the line ahead
      eye.set(x + 2.4, y + 4.4, z - ride.length / 2 - 11);
      look.set(x - 0.6, y + 1.4, z + 22);
    } else if (setting === 'tunnel') {
      eye.set(x, y + 2.3, z - (ride?.length ?? 0) / 2 - 5);
      look.set(x, y + 1.5, z + 14);
    } else {
      const back = options.mode === 'walk' ? 4.8 : options.mode === 'bus' ? 11 : setting === 'street' ? 7.5 : 16;
      const above = options.mode === 'walk' ? 0.9 : options.mode === 'bus' ? 1.2 : 2;
      // behind a walker on the pavement; behind and a little towards the middle of the road for a ride
      const across = options.mode === 'walk' ? x - 0.6 : x * 0.6;
      eye.set(across, y + height + above, z - (ride ? ride.length / 2 : 0) - back);
      look.set(options.mode === 'walk' ? x : x * 0.8, y + height * 0.6, z + 18);
    }
  };
  for (const pane of ride?.glass ?? []) pane.visible = view !== 'inside';
  aim(0);
  camera.position.copy(eye);
  camera.lookAt(look);

  const resize = () => {
    const { clientWidth: w, clientHeight: h } = element;
    if (!w || !h) return;
    renderer.setSize(w, h, false);
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
  };
  resize();
  const observer = new ResizeObserver(resize);
  observer.observe(element);

  let frame = 0;
  let last = performance.now();
  let phase = 0;
  const loop = (now: number) => {
    frame = requestAnimationFrame(loop);
    const dt = Math.min(0.1, (now - last) / 1000);
    last = now;
    const second = (Date.now() - options.startedAt) / 1000;
    const z = profile.at(second);
    const speed = profile.speedAt(second);
    keepChunks(z);
    body.position.z = z;
    // a little sway on the road; walking bobs with the steps
    if (ride) body.position.y = deck + (speed > 0.5 ? Math.sin(now / 140) * 0.012 : 0);
    if (me) {
      me.setMotion(speed > 0.2 ? 'walk' : 'idle');
      me.update(dt);
    }
    phase += dt * 6;
    for (const mover of movers) {
      mover.z += mover.speed * dt;
      // gone behind: come round again ahead; our side's traffic keeps its distance from us
      if (mover.z < z - 40) mover.z += 300;
      if (mover.z > z + 260) mover.z -= 300;
      if (mover.speed > 0 && mover.x === lane && Math.abs(mover.z - z) < 14 && options.mode !== 'walk')
        mover.z = z + 14;
      mover.group.position.z = mover.z;
      mover.step?.(phase + mover.z);
    }
    aim(z);
    // the views behind and from the side follow smoothly; the seat view is fixed to the seat
    if (view === 'inside') camera.position.copy(eye);
    else camera.position.lerp(eye, Math.min(1, dt * 4));
    camera.lookAt(look);
    renderer.render(scene, camera);
  };
  frame = requestAnimationFrame(loop);

  return {
    ok: true,
    setView: (next) => {
      view = next;
      // from the seat the windows are open to the street
      for (const pane of ride?.glass ?? []) pane.visible = next !== 'inside';
      aim(body.position.z);
      camera.position.copy(eye);
    },
    dispose: () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      me?.dispose();
      kit.dispose();
      renderer.dispose();
      canvas.remove();
    },
  };
}

// our side's traffic moves about as fast as we do, a little slower or faster
function ourSideSpeed(mode: RideMode, random: () => number) {
  return rideProfile(mode, 10).speedAt(5) * (0.9 + random() * 0.2);
}
